import { useEffect } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog"
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar"
import { useUserStore } from "@/store/user"
import { useConfigStore } from "@/store/config"
import { Wallet } from "lucide-react"
import { cn } from "@/lib/utils"
import { cancelBtn, seriesGradientBtn } from "@/lib/ui-classes"

interface InsufficientBalanceModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onRecharge: () => void
}

export default function InsufficientBalanceModal({ open, onOpenChange, onRecharge }: InsufficientBalanceModalProps) {
  const { user, fetchUser } = useUserStore()
  const config = useConfigStore((state) => state.config)
  const virtualCurrency = config?.virtual_currency || '积分'

  useEffect(() => {
    if (open) {
      fetchUser()
    }
  }, [open, fetchUser])

  if (!user) return null

  const displayName = user.nickname || user.username
  const avatarInitial = displayName?.charAt(0).toUpperCase()

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[450px] bg-zinc-900 border-zinc-800 text-white" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>{virtualCurrency}不足</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col items-center gap-6 py-6">
          <div className="relative">
            <Avatar className="h-20 w-20 border-2 border-zinc-700">
              <AvatarImage src={user.avatar_url || user.avatar || ''} alt={displayName} />
              <AvatarFallback className="bg-zinc-800 text-2xl text-zinc-400">
                {avatarInitial}
              </AvatarFallback>
            </Avatar>
          </div>

          <div className="text-center space-y-1">
            <h3 className="font-medium text-lg">{displayName}</h3>
            <p className="text-zinc-400 text-sm">当前账户{virtualCurrency}</p>
          </div>

          <div className="flex items-center gap-2 px-4 py-2 bg-zinc-950 rounded-full border border-zinc-800">
            <Wallet className="h-4 w-4 text-[#07F4F2]" />
            <span className="text-xl font-bold">{user.balance}</span>
            <span className="text-sm text-zinc-500">{virtualCurrency}</span>
          </div>

          <p className="text-zinc-400 text-sm text-center px-4">
            您的账户{virtualCurrency}不足以支付本次操作，请充值后继续。
          </p>
        </div>

        <DialogFooter className="flex gap-2 sm:justify-center w-full">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className={cn(cancelBtn, 'flex-1 h-9 !rounded-md')}
          >
            取消
          </button>
          <button
            type="button"
            onClick={() => {
              onOpenChange(false)
              onRecharge()
            }}
            className={cn(seriesGradientBtn, 'flex-1 h-9 px-4 text-sm')}
          >
            立即充值
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
